import { useEffect } from "react";

type BodyStyleSnapshot = {
  overflow: string;
  paddingRight: string;
  overscrollBehavior: string;
};

let bodyLockCount = 0;
let savedBodyStyles: BodyStyleSnapshot | null = null;
let savedHtmlStyles: BodyStyleSnapshot | null = null;

const captureStyles = (element: HTMLElement): BodyStyleSnapshot => {
  const { style } = element;
  return {
    overflow: style.overflow,
    paddingRight: style.paddingRight,
    overscrollBehavior: style.overscrollBehavior,
  };
};

const applyBodyLock = () => {
  if (bodyLockCount === 0) {
    savedBodyStyles = captureStyles(document.body);
    savedHtmlStyles = captureStyles(document.documentElement);

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const bodyStyle = document.body.style;
    const htmlStyle = document.documentElement.style;
    bodyStyle.overflow = "hidden";
    bodyStyle.overscrollBehavior = "none";
    htmlStyle.overflow = "hidden";
    htmlStyle.overscrollBehavior = "none";
    if (scrollbarWidth > 0) {
      bodyStyle.paddingRight = `${scrollbarWidth}px`;
    }
  }

  bodyLockCount += 1;
};

const releaseBodyLock = () => {
  bodyLockCount = Math.max(0, bodyLockCount - 1);
  if (bodyLockCount > 0 || !savedBodyStyles || !savedHtmlStyles) {
    return;
  }

  const bodyStyle = document.body.style;
  const htmlStyle = document.documentElement.style;
  bodyStyle.overflow = savedBodyStyles.overflow;
  bodyStyle.paddingRight = savedBodyStyles.paddingRight;
  bodyStyle.overscrollBehavior = savedBodyStyles.overscrollBehavior;
  htmlStyle.overflow = savedHtmlStyles.overflow;
  htmlStyle.paddingRight = savedHtmlStyles.paddingRight;
  htmlStyle.overscrollBehavior = savedHtmlStyles.overscrollBehavior;
  savedBodyStyles = null;
  savedHtmlStyles = null;
};

const useBodyScrollLock = (locked: boolean) => {
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }

    if (!locked) {
      bodyLockCount = 0;
      savedBodyStyles = null;
      savedHtmlStyles = null;
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.body.style.overscrollBehavior = "";
      document.documentElement.style.overflow = "";
      document.documentElement.style.paddingRight = "";
      document.documentElement.style.overscrollBehavior = "";
      return undefined;
    }

    applyBodyLock();
    return () => {
      releaseBodyLock();
    };
  }, [locked]);
};

export default useBodyScrollLock;
